import { useEffect } from "react";
import i18next from "config/i18next";
import { setAuthToken } from "middleware/api";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { actions, sessionSelector } from "reducers/session";
import { CountryCodeType } from "reducers/types/session";
import { COUNTRIES } from "utils/constants";
import { getValue } from "utils/utils";
import { actions as preliquidationActions } from "reducers/preliquidation";

import resetData from "../utils/resetData";

export const DEFAULT_COUNTRY = "AR";
const LOGIN_ROUTES = ["login", "restore", "restore-password"];

const useSession = () => {
  const { pathname } = useLocation();
  const { countryCode } = useSelector(sessionSelector);
  const tokens = getValue("tokens", true) || {};
  const dispatch = useDispatch();
  const history = useHistory();

  const sendToLogin = () => {
    history.replace(`/login`);
    dispatch(actions.resetFetching());
  };

  useEffect(() => {
    const [, params] = pathname?.split("/") as [
      undefined,
      CountryCodeType,
      string
    ];

    // Si es una ruta del login (sin country code) -> pasa
    if (LOGIN_ROUTES.includes(params)) return;

    // Si es la ruta base
    // 1 - Si no tiene tokens, lo manda al login
    // 2 - Si tiene un solo token, lo manda al dashboard correspondiente
    // 3 - Si tiene más de un token, lo manda a AR (si tiene)
    if (!params) {
      const codes = Object.keys(tokens) as CountryCodeType[];
      if (codes.length === 0) {
        sendToLogin();
      } else {
        const code = codes.length > 1 ? "AR" : codes[0];
        setAuthToken(tokens[code]);
        history.replace(`/${code}/`);
        i18next.changeLanguage(`es_${code}`);
        dispatch(actions.setCountryCode(code));
      }
      return;
    }

    const countryCodeURL = params.toUpperCase();

    const isValidCode = COUNTRIES.some(
      (c) => c.toUpperCase() === countryCodeURL
    );

    // Anotacion:
    // 0 - replace -> cuando quiero acceder a una ruta invalida
    // 1 - push    -> cuando hago un cambio valido (x ej: cambio de pais por url).

    // En caso de country code invalido obtengo el countryCode previo o el default.
    // Redirige al dashboard o al login dependiendo si ya tenia el token de dicho country.
    if (!isValidCode) {
      const code = countryCode || DEFAULT_COUNTRY;
      if (code in tokens) {
        setAuthToken(tokens[code]);
        history.replace(`/${code}/`);
      } else {
        sendToLogin();
      }
      i18next.changeLanguage(`es_${code}`);
      dispatch(actions.setCountryCode(code));
      return;
    }

    const code = (countryCodeURL as CountryCodeType) || countryCode;

    // Dirige al login cuando:
    // 1 - No tengo el token del país (requiere token)
    if (!(code in tokens)) {
      const actualCountryCode = code || DEFAULT_COUNTRY;
      i18next.changeLanguage(`es_${actualCountryCode}`);
      dispatch(actions.setCountryCode(actualCountryCode));
      sendToLogin();
      return;
    }

    // Dirige al dashboard cuando:
    // 1 - Cambio de pais a través de la URL y tenia un pais previo
    if (countryCode && countryCodeURL !== countryCode && code in tokens) {
      i18next.changeLanguage(`es_${countryCodeURL}`);
      resetData();
      setAuthToken(tokens[code]);
      dispatch(actions.setCountryCode(code));
      dispatch(preliquidationActions.resetPendingApprovalPresettlements());
      history.push(`/${code}/`);
      return;
    }

    if (code) {
      setAuthToken(tokens[code]);
      dispatch(actions.setCountryCode(code));
      i18next.changeLanguage(`es_${code}`);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pathname]);
};

export default useSession;
